// src/Components/MapTrack.js
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import StoreMarkers from "./Components/StoreMarkers";
import VehicleMarkers from "./Components/VehicleMarkers";
import useFetchUserSeller from "../Utils/useFetchUserSeller";  // Import the new hook
import { assignCustomerToRoute } from "../Utils/assignCustomerRoute";
import { useFetchData } from "../Utils/useFetchData";
import Select from "react-select";
import VehicleTrackMarkers from "./Components/VehicleTrackMarkers";
import StoreTrackMarkers from "./Components/StoreTrackMarkers";
import WaypointMarkers from "./Components/WaypointMarkers";

const MapEvents = ({ mapCenter, setMapCenter, zoom, setZoom, setZoomedIn }) => {
    const map = useMap();

    const prevMapCenter = React.useRef(mapCenter);
    const prevZoom = React.useRef(zoom);

    useEffect(() => {
        const isCenterChanged = !prevMapCenter.current || 
            Math.abs(prevMapCenter.current[0] - mapCenter[0]) > 0.000001 || 
            Math.abs(prevMapCenter.current[1] - mapCenter[1]) > 0.000001;
        const isZoomChanged = prevZoom.current !== zoom;

        if (isCenterChanged || isZoomChanged) {
            map.setView(mapCenter, zoom);
            prevMapCenter.current = mapCenter;
            prevZoom.current = zoom;
        }
    }, [mapCenter, zoom, map]);

    useEffect(() => {
        const handleMove = () => {
            const center = map.getCenter();
            const newCenter = [center.lat, center.lng];
            
            prevMapCenter.current = newCenter;
            setMapCenter(newCenter);
        };

        const handleZoom = () => {
            const currentZoom = map.getZoom();
            
            prevZoom.current = currentZoom;
            setZoom(currentZoom);
            setZoomedIn(currentZoom > 15);
        };

        map.on("moveend", handleMove);
        map.on("zoomend", handleZoom);

        return () => {
            map.off("moveend", handleMove);
            map.off("zoomend", handleZoom);
        };
    }, [map, setMapCenter, setZoom, setZoomedIn]);

    return null;
};

const Develop_Feature = ({ handleLogout }) => {

    const playbackInterval = React.useRef(null);

    const defaultPosition = [15.325319, 101.970879];
    const defaultZoom = 19;

    const getSavedMapCenter = () => {
        try {
            const raw = localStorage.getItem("mapCenter");
            if (!raw) return defaultPosition;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length === 2) {
                const lat = Number(parsed[0]);
                const lng = Number(parsed[1]);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
            if (parsed && typeof parsed === "object" && "lat" in parsed && "lng" in parsed) {
                const lat = Number(parsed.lat);
                const lng = Number(parsed.lng);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
        } catch (e) {
            // ignore and fall back
        }
        return defaultPosition;
    };

    const getSavedZoom = () => {
        try {
            const raw = localStorage.getItem("mapZoom");
            if (!raw) return defaultZoom;
            const parsed = JSON.parse(raw);
            const z = Number(parsed);
            return isFinite(z) ? z : defaultZoom;
        } catch (e) {
            return defaultZoom;
        }
    };

    const savedMapCenter = getSavedMapCenter();
    const savedZoom = getSavedZoom();
    const [error, setError] = useState("");

    const [mapCenter, setMapCenter] = useState(savedMapCenter);
    const [zoom, setZoom] = useState(savedZoom);
    const [selectedUserId, setSelectedUserId] = useState(""); // State to track selected user ID
    const [selectedDate, setSelectedDate] = useState(""); // New state for date selection
    const [storeLocations, setStoreLocations] = useState([]);
    const [vehicleLocations, setVehicleLocations] = useState([]);

    const [currentTime, setcurrentTime] = useState([]);

    const [markerPositions, setMarkerPositions] = useState({});
    const [originalPositions, setOriginalPositions] = useState({});
    const [zoomedIn, setZoomedIn] = useState(savedZoom >= 17);
    const [trackingData, setTrackingData] = useState(null); // State to store tracking data
    const [waypointData, setWaypointData] = useState(null);
    const [timeIndex, setTimeIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);


    const { fetchData } = useFetchData(handleLogout);

    // Use the custom hook for fetching user IDs
    const userIds = useFetchUserSeller(handleLogout);

    // Function to fetch tracking data with date and user ID
    const fetchTrackingData = async (userId, date) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
        /*
        if (!userId || !date) return; // Ensure both user and date are selected
        try {
            const data = await fetchData(
                `${API_BASE_URL}/api/Manager/user/${userId}/Tracking/full?date=${date}`
            );
            if (data) {
                console.log("Tracking Data:", trackingData);
                setTrackingData(data);
            }
        } catch (error) {
            console.error("Error fetching tracking data:", error);
            setError("Error fetching tracking data.");
        }
        */
    };

    // Function to fetch tracking data with date and user ID
    const fetchWaypointData = async (userId, date) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
        /*
        if (!userId || !date) return; // Ensure both user and date are selected
        try {
            const data = await fetchData(
                `${API_BASE_URL}/api/Manager/user/${userId}/navigate?date=${date}`
            );
            if (data) {
                console.log("waypoint Data:", waypointData);
                setWaypointData(data);
            }
        } catch (error) {
            console.error("Error fetching tracking data:", error);
            setError("Error fetching tracking data.");
        }
        */
    };

    const handleDeleteStoreLocation = async (storeId) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
        /*
        try {
            const response = await fetchData(
                `${API_BASE_URL}/api/Manager/saleLocations/${storeId}`,
                "DELETE"
            );

            if (response) {
                // Successfully remove the deleted store from state
                setStoreLocations((prevLocations) =>
                    prevLocations.filter((location) => location.id !== storeId)
                );
            }
        } catch (error) {
            console.error("Delete error:", error);
            setError("Error deleting store location");
        }
        */

    };

    const assignCustomerData = async (userId, storeId) => {
        try {
            const token = JSON.parse(localStorage.getItem("user") || "{}").token;
            if (!token) return;
            await assignCustomerToRoute(token, storeId, userId);
        } catch (error) {
            console.error("Assignment error:", error);
            setError("Error assigning customer data");
        }
    };

    const handlePlay = () => {
        if (isPlaying) {
            setIsPlaying(false);
            clearInterval(playbackInterval.current); // Clear existing interval
            playbackInterval.current = null;
            return;
        }

        if (trackingData && trackingData.vehLocations) {
            setIsPlaying(true);
            playbackInterval.current = setInterval(() => {
                setTimeIndex((prevIndex) => {
                    const nextIndex = prevIndex + 1;
                    if (nextIndex < trackingData.vehLocations.length) {
                        // Update map center and current time
                        const nextLocation = trackingData.vehLocations[nextIndex];
                        setMapCenter([nextLocation.latitude, nextLocation.longitude]);
                        setcurrentTime(nextLocation.time);
                        return nextIndex;
                    } else {
                        clearInterval(playbackInterval.current); // Stop playback at the end
                        playbackInterval.current = null;
                        setIsPlaying(false);
                        return prevIndex;
                    }
                });
            }, 100); // Adjust interval speed for smoother playback
        }
    };

    const handleSliderChange = (e) => {
        const newIndex = parseInt(e.target.value, 10);
        setTimeIndex(newIndex);

        // Update map center and current time immediately
        if (trackingData && trackingData.vehLocations[newIndex]) {
            const selectedLocation = trackingData.vehLocations[newIndex];
            setMapCenter([selectedLocation.latitude, selectedLocation.longitude]);
            setcurrentTime(selectedLocation.time);
        }
    };




    const handleMarkerDragEnd = (event, id) => {
        const { lat, lng } = event.target.getLatLng();
        setMarkerPositions((prevPositions) => ({
            ...prevPositions,
            [id]: {
                oldPosition: originalPositions[id],
                newPosition: [lat, lng],
            },
        }));
    };

    const resetStorePosition = (id) => {
        if (originalPositions[id]) {
            setMarkerPositions((prevPositions) => {
                const updatedPositions = { ...prevPositions };
                delete updatedPositions[id];
                return updatedPositions;
            });
        }
    };

    useEffect(() => {
        localStorage.setItem("mapCenter", JSON.stringify(mapCenter));
        localStorage.setItem("mapZoom", JSON.stringify(zoom));
    }, [mapCenter, zoom]);

    ;


    const handleUserSelect = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        if (userId === "") {
            setTrackingData(null); // Reset tracking data when "All Users" is selected
        }
    };

    return (
        <div>
            <div style={{marginBottom: "10px", fontSize: "18px", paddingLeft: "20px"}}>
                <strong>Map Center:</strong> Latitude: {Number(mapCenter?.[0]).toFixed(6)}, Longitude: {Number(mapCenter?.[1]).toFixed(6)}
                <br/>
                <strong>Zoom Level:</strong> {zoom}

                <strong> Track Time:</strong> {currentTime}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "20px" }}>
                {/* User Selection Dropdown */}
                <div style={{ maxWidth: "20%" }}>
                    <label htmlFor="user-select" style={{ display: "block", marginBottom: "5px" }}>
                        สายที่แสดงข้อมูล:
                    </label>
                    <Select
                        id="user-select"
                        options={[
                            { value: "", label: "เลือกสายที่จะแสดงข้อมูล" }, // Default option for all users
                            ...userIds.map((user) => ({
                                value: user.uid,
                                label: `${user.nameuser} (${user.username})`,
                            })),
                        ]}
                        value={
                            selectedUserId
                                ? {
                                    value: selectedUserId,
                                    label:
                                        userIds.find((user) => user.uid === selectedUserId)?.nameuser +
                                        " (" +
                                        userIds.find((user) => user.uid === selectedUserId)?.username +
                                        ")",
                                }
                                : { value: "", label: "แสดงทุกสาย" }
                        }
                        onChange={(selectedOption) => {
                            const userId = selectedOption ? selectedOption.value : "";
                            setSelectedUserId(userId);
                            if (userId === "") {
                                setTrackingData(null);
                            }
                        }}
                        formatOptionLabel={(option) => (
                            <div>
                                <span style={{ fontWeight: "bold" }}>{option.label}</span>
                            </div>
                        )}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isClearable
                    />
                </div>

                {/* Date Selection */}
                <div style={{ maxWidth: "20%" }}>
                    <label htmlFor="date-select" style={{ display: "block", marginBottom: "5px" }}>
                        เลือกวันที่:
                    </label>
                    <input
                        id="date-select"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{ width: "100%", padding: "5px", fontSize: "14px" }}
                    />
                </div>

                {/* Confirm Button */}
                <div>
                    <button
                        onClick={() => {fetchTrackingData(selectedUserId, selectedDate) ; fetchWaypointData(selectedUserId,selectedDate) }}
                        disabled={!selectedUserId || !selectedDate}
                        style={{
                            padding: "10px 20px",
                            fontSize: "16px",
                            backgroundColor: selectedUserId && selectedDate ? "#007BFF" : "#CCC",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: selectedUserId && selectedDate ? "pointer" : "not-allowed",
                        }}
                    >
                        ประมวลผลเส้นทาง
                    </button>
                </div>
            </div>

            <MapContainer center={mapCenter} key="dashmap" zoom={zoom}
                          style={{height: "650px", width: "100%", zIndex: "15"}}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapEvents
                    mapCenter={mapCenter}
                    setMapCenter={setMapCenter}
                    zoom={zoom}
                    setZoom={setZoom}
                    setZoomedIn={setZoomedIn}
                />
                {/* Display store and vehicle locations based on tracking data */}
                {trackingData ? (
                    <>
                        <StoreTrackMarkers
                            storeLocations={trackingData.customerData} // Display store locations for selected user
                            markerPositions={markerPositions}
                            zoomedIn={zoomedIn}
                            handleMarkerDragEnd={handleMarkerDragEnd}
                            handleResetStorePosition={resetStorePosition}
                            handleDeleteStoreLocation={handleDeleteStoreLocation}
                            currenttime = {currentTime}
                        />

                        <WaypointMarkers
                            storeLocations={waypointData} // Display store locations for selected user
                            markerPositions={markerPositions}
                            zoomedIn={zoomedIn}
                            currenttime = {currentTime}
                        />

                        {trackingData && trackingData.vehLocations ? (
                            <VehicleTrackMarkers
                                vehicleLocations={trackingData.vehLocations}
                                timeIndex={timeIndex}
                                isPlaying={isPlaying}
                            />
                        ) : null}
                    </>
                ) : null }
            </MapContainer>

            {trackingData && trackingData.vehLocations && (
                <div style={{ marginTop: "10px" }}>
                    <input
                        type="range"
                        min="0"
                        max={trackingData.vehLocations.length - 1}
                        value={timeIndex}
                        onChange={handleSliderChange}
                        style={{ width: "100%" }}
                    />
                    <button onClick={handlePlay} style={{ marginLeft: "10px" }}>
                        {isPlaying ? "หยุด" : "เล่น"}
                    </button>
                </div>
            )}

        </div>


    );


};

export default Develop_Feature;
